import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import FilterControls from '../../components/Dashboard/FilterControls';
import KPICards from '../../components/Dashboard/KPICards';
import DoctorList from '../../components/Dashboard/DoctorList';
import PatientList from '../../components/Dashboard/PatientList';
import DoctorLeaveList from '../../components/Dashboard/DoctorLeaveList';
import AppointmentList from '../../components/Dashboard/AppointmentList';
import { ActionGetDashboardData } from '../../store/Actions/CommonAction';
import { Spin } from 'antd';
import { matchPermissions } from '../../utils/permissionHelper';
import { PERMISSIONS } from '../../constants';

const ClinicOperationsDashboard = () => {
  const dispatch = useDispatch();
  const { getDashboardData, loader } = useSelector(state => state?.Common);
  const { userDetails } = useSelector(state => state?.auth);
  const isDoctor = userDetails?.[0]?.type === 'Doctor';

  const matchPermissionsForUser = matchPermissions(userDetails);

  const canViewStats = matchPermissionsForUser(PERMISSIONS.DASHBOARD.CLINIC_STATS_VIEW);
  const canViewAppointments = matchPermissionsForUser(PERMISSIONS.DASHBOARD.CLINIC_APPOINTMENTS_VIEW);
  const canViewDoctors = matchPermissionsForUser(PERMISSIONS.DASHBOARD.CLINIC_DOCTORS_VIEW);
  const canViewPatients = matchPermissionsForUser(PERMISSIONS.DASHBOARD.CLINIC_PATIENTS_VIEW);
  const canViewDoctorLeaves = matchPermissionsForUser(PERMISSIONS.DASHBOARD.CLINIC_DOCTOR_LEAVES_VIEW);

  const [searchParams] = useSearchParams();
  const isInitialLoad = useRef(true);

  // Build query string from filters
  const buildApiPayload = (filters) => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        queryParams.append(key, value);
      }
    });

    const queryString = queryParams.toString();
    return {
      Query: queryString ? `?${queryString}` : '',
      obj: {}
    };
  };

  useEffect(() => {
    // Only call API on initial mount
    if (isInitialLoad.current) {
      // Extract filters from URL params
      const urlFilters = {
        orderStartDate: searchParams.get('orderStartDate') || '',
        orderEndDate: searchParams.get('orderEndDate') || '',
        doctorId: searchParams.get('doctorId') || '',
        status: searchParams.get('status') || '',
        search: searchParams.get('search') || '',
      };

      const apiPayload = buildApiPayload(urlFilters);
      dispatch(ActionGetDashboardData(apiPayload));
      isInitialLoad.current = false;
    }
  }, [dispatch]);

  const handleFilterChange = newFilters => {
    const apiPayload = buildApiPayload(newFilters);
    dispatch(ActionGetDashboardData(apiPayload));
  };

  return (
    <>
      <Header />
      <Sidebar />
      <div className={`page-wrapper ${loader ? 'loader-active' : ''}`}>
        {loader && <div className="loader-overlay"></div>}
        {loader && (
          <div className="loader-container">
            <Spin size="large" />
          </div>
        )}
        <div className="container-fluid p-3 p-md-4">
          <div className="page-header mb-3 mb-md-4">
            <h1 className="h3 mb-3">CLINIC OPERATIONS DASHBOARD</h1>
            <p className="text-muted mb-0 small">Comprehensive overview of clinic metrics, appointments, and patient data</p>
          </div>

          <FilterControls onFilterChange={handleFilterChange} isDoctor={isDoctor} />

          {canViewStats && <KPICards data={getDashboardData?.counts?.[0]} isDoctor={isDoctor} />}

          {canViewAppointments && (
            <div className="row">
              <div className="col-12">
                <AppointmentList appointments={getDashboardData?.todaysAppointment} />
              </div>
            </div>
          )}

          {!isDoctor &&
            <>

              <div className="row g-3 g-xl-4">
                <div className="col-12 col-xl-6">
                  {canViewDoctors ? (
                    <DoctorList doctors={getDashboardData?.availableDoctorList} />
                  ) : (
                    <div className="permission-denied-message">
                      No permission to view doctors.
                    </div>
                  )}
                </div>
                <div className="col-12 col-xl-6">
                  {canViewPatients ? (
                    <PatientList patients={getDashboardData?.enrollPatients} />
                  ) : (
                    <div className="permission-denied-message">
                      No permission to view patients.
                    </div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  {canViewDoctorLeaves ? (
                    <DoctorLeaveList leaves={getDashboardData?.doctorsOnLeaveCount} />
                  ) : (
                    <div className="permission-denied-message">
                      No permission to view doctor leaves.
                    </div>
                  )}
                </div>
              </div>
            </>
          }

        </div>
      </div>
    </>
  );
};

export default ClinicOperationsDashboard;
